import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import colorsStore from "shared/store/colorsStore";
import { ProductCardArrayDateValueType } from "types/AssistantTypes";
import styles from "./drawdown.module.scss";
import { formatLocalizedDate } from "utils/dateUtils";
import { formatItalianNumber } from "utils/formatUtils";

type DrawdownChartProps = {
  data: ProductCardArrayDateValueType[];
};

// Determine appropriate interval for X-axis based on data length
const getXAxisInterval = (dataLength: number) => {
  if (dataLength > 120) return Math.ceil(dataLength / 12); // Show approximately 12 labels max
  if (dataLength > 60) return 12; // Annual for 5+ years of data
  if (dataLength > 24) return 6; // Semi-annual for 2+ years
  if (dataLength > 12) return 3; // Quarterly for 1+ year
  return 1; // Monthly for less than a year
};

const DrawdownChart: React.FC<DrawdownChartProps> = ({ data }) => {
  const { t, i18n } = useTranslation();
  const formattedData = useMemo(() => {
    return data.map((item) => ({
      ...item,
      value: item.value * 1,
      tooltipDate: formatLocalizedDate(
        new Date(item.date),
        i18n.language,
        "dd MMM yyyy",
      ),
      displayDate: formatLocalizedDate(
        new Date(item.date),
        i18n.language,
        "MMM yyyy",
      ),
    }));
  }, [data, i18n.language]);

  const colorsConfig = colorsStore((state) => state.colorsConfigStore);
  const chartCopy = {
    fontSize: 11,
    fill: colorsConfig.finecoBlack,
    fontFamily: "Arial",
  };
  const { minValue, maxValue, yAxisTicks } = useMemo(() => {
    const values = formattedData.map((item) => item.value);
    const min = Math.floor(Math.min(...values) / 5) * 5; // Round down to nearest 5
    const max = Math.ceil(Math.max(...values) / 5) * 5; // Round up to nearest 5

    // Generate ticks every 5%
    const ticks = [];
    for (let i = min; i <= max; i += 5) {
      ticks.push(i);
    }

    return {
      minValue: min,
      maxValue: max,
      yAxisTicks: ticks,
    };
  }, [formattedData]);

  const xAxisInterval = useMemo(
    () => getXAxisInterval(formattedData.length),
    [formattedData],
  );

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const entry = payload[0];
      const formattedDate = formatLocalizedDate(
        new Date(entry.payload.date),
        i18n.language,
        "dd MMM yyyy",
      );
      return (
        <div
          className={styles.tooltip}
          style={{
            backgroundColor: "white",
            border: `1px solid ${colorsConfig.FinecoGrayLight1}`,
            padding: "8px 10px",
            borderRadius: "4px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          }}
        >
          <p className={styles.tooltipLabel}>{formattedDate}</p>
          <p
            className={styles.tooltipLabel}
            style={{
              color: entry.color,
              margin: "3px 0",
              // fontSize: "12px",
            }}
          >
            {t(`portfolio.${entry.name}`)}: {`${formatItalianNumber(entry.value, 2)}%`}
          </p>
        </div>
      );
    }
    return null;
  };

  if (data.length === 0) {
    return null;
  }

  return (
    <div className={styles.sectionContainer}>
      <div className={styles.chartContainer}>
        <ResponsiveContainer debounce={500}>
          <AreaChart
            data={formattedData}
            margin={{ top: 10, right: 10, left: -25, bottom: 0 }}
          >
            <defs>
              <linearGradient id="gradientFill" x1="0" y1="0" x2="0" y2="1">
                <stop
                  offset="0%"
                  stopColor="rgb(255, 255, 255)"
                  stopOpacity={1}
                />
                <stop
                  offset="80%"
                  stopColor={colorsConfig.BlueLight}
                  stopOpacity={1}
                />
              </linearGradient>
            </defs>
            <CartesianGrid
              strokeWidth={1}
              stroke={colorsConfig.GrayLight2}
              strokeOpacity={1}
              horizontal={true}
              vertical={false}
            />
            <XAxis
              dataKey="displayDate"
              dx={40}
              dy={10}
              angle={-20}
              textAnchor="end"
              height={60}
              interval={xAxisInterval}
              tick={chartCopy}
              axisLine={{ stroke: colorsConfig.GrayLight2 }}
              tickLine={false}
            />
            <YAxis
              tick={chartCopy}
              tickFormatter={(value) => `${value}%`}
              domain={[minValue, maxValue]}
              ticks={yAxisTicks}
              axisLine={{ stroke: colorsConfig.GrayLight2 }}
              tickLine={false}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              content={(props) => <CustomizedLegend {...props} t={t} />}
              verticalAlign="bottom"
              height={0}
            />
            <Area
              type="basis"
              dataKey="value"
              className={styles.areaPath}
              stroke={colorsConfig.BlueLight}
              strokeWidth={1}
              fill="url(#gradientFill)"
              isAnimationActive={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default DrawdownChart;

const CustomizedLegend = React.memo(
  ({ payload, t }: { payload?: any[]; t: (key: string) => string }) => {
    return (
      <div className={styles.legendContainer}>
        {payload?.map((entry, index) => (
          <div key={`item-${index}`} className={styles.legendItem}>
            <div
              className={styles.legendDot}
              style={{ backgroundColor: entry.color }}
            />
            <span className={styles.legendCopy}>
              {t("productCard.drawdown")}
            </span>
          </div>
        ))}
      </div>
    );
  },
);
